import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { DATE_RANGE, OWNER, PROPERTY } from "./consts";
import { useGetPropertiesByCommunity, useGetPropertyOccupancyListByCommunity, useGetTransactionHistory } from "@/services";
import moment from "moment";
import { billingHistoryStatus, cn, formatAmount, getRequiredFields, MONTHS, transactionTypeValues, toApiSort } from "@/lib/utils";
import { Badge } from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";

const useReportByOwner = () => {
  const {communityData, currentFinanceYear} = useSelector((state: RootState) => state.currentData);
  const communityName = communityData?.name || communityData?.communityName || "";
  const isRunBtnClicked = useRef<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  
  const form = useForm<formValues>({
    defaultValues: {
      [PROPERTY]: "",
      [OWNER]: "",
      [DATE_RANGE]: {from: null, to: null},
    },
    resolver: zodResolver(schema)
  });
  
  const requiredFields = getRequiredFields(schema);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.balanceReport}`,
        name: `Balance Report`,
      },
      {
        link: "",
        name: <span className="text-primary">Report By Owner</span>,
      },
    ],
    [communityName]
  );

  const title = useMemo(() => <span>{`Report By Owner - ${communityName}`}<span className="text-[12px]">(FY-{currentFinanceYear})</span></span>,[communityName]);

  const formData = useWatch({control: form.control});

  const {data: ownerList, isLoading: isOwnerLoading} = useGetPropertyOccupancyListByCommunity({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });

  const {data: propertyList, isLoading: isPropertyLoading} = useGetPropertiesByCommunity({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });
  
  const propertyOptions = useMemo(() => {
    const list = formData[OWNER]?.length ? 
    propertyList?.data
    ?.filter((data: any) => formData[OWNER]?.includes(data?.ownershipId?.toString())) : propertyList?.data;

    return list
    ?.map((data: any) => ({
      label: data?.name,
      value: data?.propertyId?.toString()
    }))
  }, [propertyList, formData[OWNER]]);

  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetTransactionHistory({
    communityId: communityData?.communityId,
    data: {
      filters: [
        {
          field: "communityId",
          value: communityData?.communityId,
          operator: "EQUAL",
        },
        ...(
          formData[PROPERTY] ? [{
            field: PROPERTY,
            value: formData[PROPERTY],
            operator: "EQUAL",
          }] : []
        ),
        ...(formData[OWNER] ? [{
          field: OWNER,
          value: formData[OWNER],
          operator: "EQUAL",
        }] : []),
        ...(formData[DATE_RANGE]?.from &&
          formData[DATE_RANGE]?.to
          ? [
              {
                field: DATE_RANGE,
                value: [
                  moment(form.watch()[DATE_RANGE]?.from).format(
                    "YYYY-MM-DDT00:00:00"
                  ),
                  moment(form.watch()[DATE_RANGE]?.to).format(
                    "YYYY-MM-DDT23:59:59"
                  ),
                ],
                operator: "BETWEEN",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: false,
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);
  
  const onRunReport = () => {
    isRunBtnClicked.current = true;
    refetchDoc();
  }

  const onClear = () => {
    form.reset({
      [PROPERTY]: "",
      [OWNER]: "",
      [DATE_RANGE]: {from: null, to: null},
    })
    setPageNumber(0);
    setPageSize(10);
    isRunBtnClicked.current = false;
  }

  // Define Table Columns
  const columns = [
    {
      accessorKey: "updatedAt",
      header: <div>Transaction Date</div>,
      accessorFn: (row: any) =>
        row?.updatedAt ? moment(row?.updatedAt || "").format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "transactionType",
      header: <div>Transaction Type</div>,
      accessorFn: (row: any) =>
        (transactionTypeValues as any)[String(row?.transactionType || "")],
    },
    {
      accessorKey: "billingCategory",
      header: <div>Billing Category</div>,
    },
    {
      accessorKey: "month",
      header: <div>Billing Month</div>,
      accessorFn: (row: any) => MONTHS[row.month - 1],
    },
    {
      accessorKey: "debit",
      meta: { align: "right" },
      header: <div>Debits</div>,
      accessorFn: (row: any) => formatAmount(row?.debit || 0),
    },
    {
      accessorKey: "credit",
      meta: { align: "right" },
      header: <div>Credits</div>,
      accessorFn: (row: any) =>
        formatAmount(row?.credit ? row?.credit : row?.writeOff || 0),
    },
    {
      accessorKey: "balance",
      meta: { align: "right" },
      header: <div>Balance</div>,
      accessorFn: (row: any) => formatAmount(row?.balance || 0),
    },
    {
      accessorKey: "remark",
      header: <div>Remark</div>,
      accessorFn: (row: any) => String(row?.remark || ""),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={cn(
                billingHistoryStatus[row?.row?.original?.status?.toLowerCase()]
                  ?.color,
                "rounded-full px-4 py-2"
              )}
            >
              {
                billingHistoryStatus[row?.row?.original?.status?.toLowerCase()]
                  ?.label
              }
            </Badge>
          </div>
        );
      },
    },
  ];

  return {
    breadcrumbData,
    title,
    form,
    propertyOptions,
    ownerList: ownerList?.data,
    isLoading: isOwnerLoading || isPropertyLoading,
    onRunReport,
    onClear,
    isDocLoading: isDocLoading || isDocRefetching,
    columns,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    documentList,
    isRunBtnClicked: isRunBtnClicked.current,
    requiredFields
  }
}

export default useReportByOwner;
